<template>
  <modal
    transition="nice-modal-fade"
    width="720"
    height="auto"
    :adaptive="true"
    :reset="true"
    :scrollable="false"
    :name="modalName"
    class="listing--form-modal recommended-listing-vmodal"
    @opened="opened"
    @before-open="beforeOpen"
  >
    <div class="recommended-modal">
      <section-loading :show="loading" />

      <!-- Header -->
      <header class="recommended-modal__header">
        <button
          type="button"
          class="recommended-modal__close"
          aria-label="Tutup modal"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </button>

        <div class="recommended-modal__heading">
          <div class="recommended-modal__icon">
            <v-icon>mdi-star-outline</v-icon>
          </div>

          <div class="recommended-modal__heading-content">
            <h2 class="recommended-modal__title">
              {{ $t('listing.modal.recommendedListing.title') }}
            </h2>

            <p class="recommended-modal__description">
              Pilih periode penayangan agar listing mendapatkan eksposur lebih tinggi kepada calon
              pembeli atau penyewa.
            </p>
          </div>
        </div>

        <!-- Step Indicator -->
        <div class="recommended-steps">
          <div
            class="recommended-step"
            :class="{
              'recommended-step--active': step === 1,
              'recommended-step--completed': step > 1,
            }"
          >
            <div class="recommended-step__number">
              <v-icon v-if="step > 1">mdi-check</v-icon>
              <span v-else>1</span>
            </div>

            <div class="recommended-step__content">
              <strong>Pilih Periode</strong>
              <span>Tentukan jadwal rekomendasi</span>
            </div>
          </div>

          <div
            class="recommended-steps__line"
            :class="{
              'recommended-steps__line--active': step > 1,
            }"
          />

          <div
            class="recommended-step"
            :class="{
              'recommended-step--active': step === 2,
            }"
          >
            <div class="recommended-step__number">
              <span>2</span>
            </div>

            <div class="recommended-step__content">
              <strong>Konfirmasi</strong>
              <span>Periksa dan lanjutkan</span>
            </div>
          </div>
        </div>
      </header>

      <!-- Scrollable Body -->
      <main class="recommended-modal__body">
        <!-- Step 1 -->
        <template v-if="step === 1">
          <!-- Listing Summary -->
          <section class="listing-summary">
            <div class="section-heading">
              <div>
                <span class="section-heading__eyebrow"> Informasi Listing </span>

                <h3 class="section-heading__title">
                  {{ $t('listing.modal.recommendedListing.info') }}
                </h3>
              </div>

              <div class="listing-summary__type">
                <v-icon>
                  {{ listingType === 'S' ? 'mdi-tag-outline' : 'mdi-key-outline' }}
                </v-icon>

                <span>{{ listingTypeLabel }}</span>
              </div>
            </div>

            <div class="listing-summary__grid">
              <div class="listing-summary__item">
                <div class="listing-summary__item-icon">
                  <v-icon>mdi-home-city-outline</v-icon>
                </div>

                <div class="listing-summary__item-content">
                  <span class="listing-summary__label">
                    {{ $t('general.propertyType') }}
                  </span>

                  <strong class="listing-summary__value">
                    {{ propertyType || '-' }}
                  </strong>
                </div>
              </div>

              <div class="listing-summary__item">
                <div class="listing-summary__item-icon">
                  <v-icon>mdi-map-outline</v-icon>
                </div>

                <div class="listing-summary__item-content">
                  <span class="listing-summary__label">
                    {{ $t('general.province') }}
                  </span>

                  <strong class="listing-summary__value">
                    {{ province || '-' }}
                  </strong>
                </div>
              </div>

              <div class="listing-summary__item">
                <div class="listing-summary__item-icon">
                  <v-icon>mdi-city-variant-outline</v-icon>
                </div>

                <div class="listing-summary__item-content">
                  <span class="listing-summary__label">
                    {{ $t('general.city') }}
                  </span>

                  <strong class="listing-summary__value">
                    {{ city || '-' }}
                  </strong>
                </div>
              </div>

              <div class="listing-summary__item">
                <div class="listing-summary__item-icon">
                  <v-icon>mdi-map-marker-outline</v-icon>
                </div>

                <div class="listing-summary__item-content">
                  <span class="listing-summary__label">
                    {{ $t('general.area') }}
                  </span>

                  <strong class="listing-summary__value">
                    {{ area || '-' }}
                  </strong>
                </div>
              </div>
            </div>
          </section>

          <!-- Availability -->
          <section class="availability-section">
            <div class="section-heading availability-section__heading">
              <div>
                <span class="section-heading__eyebrow"> Periode Tersedia </span>

                <h3 class="section-heading__title">
                  {{ $t('recommendedListing.selectSlot') }}
                </h3>

                <p class="section-heading__description">
                  Anda dapat memilih lebih dari satu periode rekomendasi.
                </p>
              </div>

              <div v-if="selectedAvailabilities.length > 0" class="availability-section__count">
                <v-icon>mdi-check-circle</v-icon>

                <span> {{ selectedAvailabilities.length }} dipilih </span>
              </div>
            </div>

            <div v-if="availabilities && availabilities.length > 0" class="availability-grid">
              <button
                v-for="availability in availabilities"
                :key="availability.uuid"
                type="button"
                class="availability-card"
                :class="{
                  'availability-card--selected': checkIfSelected(availability),
                  'availability-card--disabled': checkIfDisabled(availability),
                }"
                :disabled="checkIfDisabled(availability)"
                :aria-pressed="checkIfSelected(availability)"
                @click="selectSlot(availability)"
              >
                <div class="availability-card__header">
                  <div class="availability-card__icon">
                    <v-icon>mdi-calendar-blank-outline</v-icon>
                  </div>

                  <span
                    v-if="checkIfSelected(availability)"
                    class="availability-card__status availability-card__status--selected"
                  >
                    <v-icon>mdi-check</v-icon>
                    Dipilih
                  </span>

                  <span
                    v-else-if="checkIfBought(availability)"
                    class="availability-card__status availability-card__status--disabled"
                  >
                    {{ $t('recommendedListing.alreadyBought') }}
                  </span>

                  <span
                    v-else-if="availability.available_slot <= 0"
                    class="availability-card__status availability-card__status--disabled"
                  >
                    Slot penuh
                  </span>
                </div>

                <div class="availability-card__content">
                  <span class="availability-card__label"> Periode rekomendasi </span>

                  <strong class="availability-card__date">
                    {{ $date.format(availability.start_date, 'd MMM yyyy') }}
                  </strong>

                  <div class="availability-card__separator">
                    <span />
                    <v-icon>mdi-arrow-down</v-icon>
                    <span />
                  </div>

                  <strong class="availability-card__date">
                    {{ $date.format(availability.end_date, 'd MMM yyyy') }}
                  </strong>
                </div>

                <div class="availability-card__footer">
                  <div class="availability-card__remaining">
                    <v-icon>mdi-view-grid-outline</v-icon>

                    <span>
                      {{
                        availability.available_slot > 0
                          ? `${$t('recommendedListing.remaining')}: ${availability.available_slot}`
                          : 'Tidak tersedia'
                      }}
                    </span>
                  </div>

                  <!-- Harga promo dan harga awal -->
                  <div v-if="!checkIfDisabled(availability)" class="availability-card__price">
                    <div class="availability-card__price-current">
                      <strong>{{ discountedCoinCost }}</strong>
                      <span>{{ $t('general.coin') }}</span>
                    </div>

                    <div class="availability-card__price-original">
                      <span>{{ originalCoinCost }}</span>
                      <span>{{ $t('general.coin') }}</span>
                    </div>
                  </div>
                </div>
              </button>
            </div>

            <div v-else class="availability-empty">
              <div class="availability-empty__icon">
                <v-icon>mdi-calendar-remove-outline</v-icon>
              </div>

              <h4>Belum ada periode tersedia</h4>

              <p>Periode rekomendasi untuk listing ini sedang tidak tersedia.</p>
            </div>
          </section>
        </template>

        <!-- Step 2 -->
        <template v-else>
          <section class="confirmation-section">
            <div class="confirmation-section__heading">
              <div class="confirmation-section__heading-icon">
                <v-icon>mdi-clipboard-check-outline</v-icon>
              </div>

              <div>
                <span class="section-heading__eyebrow"> Ringkasan Pesanan </span>

                <h3 class="section-heading__title">
                  {{ $t('listing.modal.recommendedListing.confirm') }}
                </h3>

                <p class="section-heading__description">
                  Pastikan periode dan jumlah koin yang digunakan sudah sesuai.
                </p>
              </div>
            </div>

            <!-- Selected Periods -->
            <div class="confirmation-periods">
              <div class="confirmation-periods__header">
                <div>
                  <span class="confirmation-periods__label"> Periode terpilih </span>

                  <strong>
                    {{
                      `${selectedAvailabilities.length} ${$t(
                        'listing.modal.recommendedListing.slot',
                      )}`
                    }}
                  </strong>
                </div>

                <button type="button" class="confirmation-periods__edit" @click="step = 1">
                  <v-icon>mdi-pencil-outline</v-icon>
                  <span>Ubah</span>
                </button>
              </div>

              <div class="confirmation-periods__list">
                <div
                  v-for="(availability, index) in selectedAvailabilities"
                  :key="availability.uuid"
                  class="confirmation-period"
                >
                  <div class="confirmation-period__number">
                    {{ index + 1 }}
                  </div>

                  <div class="confirmation-period__date">
                    <span>Periode rekomendasi</span>

                    <strong>
                      {{ $date.format(availability.start_date, 'd MMM yyyy') }}
                      -
                      {{ $date.format(availability.end_date, 'd MMM yyyy') }}
                    </strong>
                  </div>

                  <div class="confirmation-period__price">
                    <div class="confirmation-period__current-price">
                      <strong>{{ discountedCoinCost }}</strong>
                      <span>{{ $t('general.coin') }}</span>
                    </div>

                    <del> {{ originalCoinCost }} {{ $t('general.coin') }} </del>
                  </div>
                </div>
              </div>
            </div>

            <!-- Coin Summary -->
            <div class="coin-summary">
              <div class="coin-summary__header">
                <div class="coin-summary__header-icon">
                  <v-icon>mdi-circle-multiple-outline</v-icon>
                </div>

                <div>
                  <span>
                    {{ $t('listing.modal.recommendedListing.usedCoin') }}
                  </span>

                  <strong>Rincian penggunaan koin</strong>
                </div>
              </div>

              <div class="coin-summary__rows">
                <div class="coin-summary__row">
                  <span>Jumlah slot yang dipilih</span>

                  <strong>
                    {{ selectedAvailabilities.length }}
                    {{ $t('listing.modal.recommendedListing.slot') }}
                  </strong>
                </div>

                <div class="coin-summary__row">
                  <span>Harga normal</span>

                  <del>
                    {{ originalTotalCoinCost }}
                    {{ $t('general.coin') }}
                  </del>
                </div>

                <div class="coin-summary__row coin-summary__row--discount">
                  <span>Potongan promo</span>

                  <strong>
                    -{{ totalCoinDiscount }}
                    {{ $t('general.coin') }}
                  </strong>
                </div>
              </div>

              <div class="coin-summary__total">
                <div class="coin-summary__total-info">
                  <span>Total koin digunakan</span>

                  <small>
                    Saldo Anda:
                    {{ totalCoins }}
                    {{ $t('general.coin') }}
                  </small>
                </div>

                <div class="coin-summary__total-price">
                  <strong>{{ discountedTotalCoinCost }}</strong>
                  <span>{{ $t('general.coin') }}</span>
                </div>
              </div>
            </div>
          </section>
        </template>
      </main>

      <!-- Fixed Footer -->
      <footer class="recommended-modal__footer">
        <button
          v-if="step === 2"
          type="button"
          class="recommended-button recommended-button--secondary"
          :disabled="loading"
          @click="step = 1"
        >
          <v-icon>mdi-arrow-left</v-icon>

          <span>
            {{ $t('listing.modal.recommendedListing.back') }}
          </span>
        </button>

        <div v-else class="recommended-modal__selection-info">
          <v-icon>mdi-information-outline</v-icon>

          <span>
            {{
              selectedAvailabilities.length > 0
                ? `${selectedAvailabilities.length} periode telah dipilih`
                : 'Pilih minimal satu periode untuk melanjutkan'
            }}
          </span>
        </div>

        <button
          type="button"
          class="recommended-button recommended-button--primary"
          :disabled="loading || selectedAvailabilities.length === 0"
          @click="recommendedListing"
        >
          <span>
            {{ step === 1 ? 'Lanjutkan' : $t('listing.modal.recommendedListing.btn') }}
          </span>

          <v-icon>
            {{ step === 1 ? 'mdi-arrow-right' : 'mdi-check-circle-outline' }}
          </v-icon>
        </button>
      </footer>
    </div>
  </modal>
</template>

<script>
import { mapState } from 'vuex';

const SectionLoading = () => import('@/components/content-loading/section-loading');

export default {
  name: 'recommended-listing-modal',

  components: {
    SectionLoading,
  },

  data() {
    return {
      modalName: 'recommended-listing-modal',
      submitAction: null,
      step: 1,
      listingUuid: null,
    };
  },

  beforeDestroy() {
    this.showTawkWidget();
  },

  computed: {
    ...mapState({
      loading: (state) => state.coins.loading,
      totalCoins: (state) => state.coins.totalCoins,
      availabilities: (state) => state.recommendedListing.availabilities,
      province: (state) => state.recommendedListing.province,
      city: (state) => state.recommendedListing.city,
      area: (state) => state.recommendedListing.area,
      propertyType: (state) => state.recommendedListing.propertyType,
      listingType: (state) => state.recommendedListing.listingType,
    }),

    selectedAvailabilities: {
      get() {
        return this.$store.state.recommendedListing.selectedAvailabilities || [];
      },

      set(value) {
        this.$store.commit('recommendedListing/SET_SELECTED_AVAILABILITIES', value);
      },
    },

    listingTypeLabel() {
      return this.listingType === 'S' ? this.$t('general.sell') : this.$t('general.rent');
    },

    discountedCoinCost() {
      return 25;
    },

    originalCoinCost() {
      return 50;
    },

    discountedTotalCoinCost() {
      return this.selectedAvailabilities.length * this.discountedCoinCost;
    },

    originalTotalCoinCost() {
      return this.selectedAvailabilities.length * this.originalCoinCost;
    },

    totalCoinDiscount() {
      return this.originalTotalCoinCost - this.discountedTotalCoinCost;
    },
  },

  methods: {
    close() {
      this.showTawkWidget();
      this.$modal.hide(this.modalName);
    },

    beforeOpen(event) {
      this.hideTawkWidget();
      this.step = 1;
      this.submitAction = null;
      this.listingUuid = null;

      if (!event.params) {
        return;
      }

      if (typeof event.params.submitAction === 'function') {
        this.submitAction = event.params.submitAction;
      }

      if (event.params.listingUuid) {
        this.listingUuid = event.params.listingUuid;
      }
    },

    async opened() {
      try {
        await this.$store.dispatch('coins/getTotalCoins');

        await this.$store.dispatch('recommendedListing/restoreInitialState');

        await this.$store.dispatch('recommendedListing/getForm', this.listingUuid);
      } catch (error) {
        console.error('Error opening recommended listing modal:', error);

        this.close();
      }
    },

    hideTawkWidget() {
      if (typeof document !== 'undefined') {
        document.body.classList.add('hide-tawk-widget');
      }

      if (typeof window !== 'undefined' && window.Tawk_API && window.Tawk_API.hideWidget) {
        window.Tawk_API.hideWidget();
      }
    },

    showTawkWidget() {
      if (typeof document !== 'undefined') {
        document.body.classList.remove('hide-tawk-widget');
      }

      if (typeof window !== 'undefined' && window.Tawk_API && window.Tawk_API.showWidget) {
        window.Tawk_API.showWidget();
      }
    },

    checkIfSelected(availability) {
      return this.selectedAvailabilities.some((item) => item.uuid === availability.uuid);
    },

    checkIfDisabled(availability) {
      return availability.already_bought || availability.available_slot <= 0;
    },

    checkIfBought(availability) {
      return availability.already_bought;
    },

    selectSlot(availability) {
      if (this.checkIfDisabled(availability)) {
        return;
      }

      if (this.checkIfSelected(availability)) {
        this.selectedAvailabilities = this.selectedAvailabilities.filter(
          (item) => item.uuid !== availability.uuid,
        );

        return;
      }

      this.selectedAvailabilities = [...this.selectedAvailabilities, availability];
    },

    async recommendedListing() {
      if (this.loading || this.selectedAvailabilities.length === 0) {
        return;
      }

      if (this.step === 1) {
        this.step = 2;
        return;
      }

      this.$emit('submit');

      if (this.submitAction) {
        this.submitAction();
      }

      this.close();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../assets/scss/utils/variables';

.recommended-modal {
  --rl-red: #c81919;
  --rl-red-dark: #9f1313;
  --rl-heading: #18191c;
  --rl-text: #2c2e32;
  --rl-muted: #6d7178;
  --rl-border: #e3e5e8;
  --rl-border-dark: #d5d8dd;
  --rl-surface: #ffffff;
  --rl-surface-soft: #f7f8fa;

  position: relative;
  width: 100%;
  height: min(830px, calc(100vh - 32px));
  min-height: 0;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  border: 1px solid rgba(25, 27, 30, 0.08);
  border-radius: 22px;

  color: var(--rl-text);
  background: var(--rl-surface);

  font-family: Poppins-Regular, Poppins, sans-serif;

  box-shadow: 0 28px 70px rgba(22, 24, 27, 0.18), 0 8px 20px rgba(22, 24, 27, 0.07);
}

/* Header */

.recommended-modal__header {
  position: relative;
  z-index: 3;

  flex: 0 0 auto;

  padding: 27px 30px 24px;

  border-bottom: 1px solid var(--rl-border);
  background: #ffffff;
}

.recommended-modal__close {
  position: absolute;
  z-index: 5;
  top: 19px;
  right: 19px;

  width: 40px;
  height: 40px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: 1px solid var(--rl-border);
  border-radius: 12px;

  color: #5f6268;
  background: #ffffff;

  cursor: pointer;

  transition: color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease,
    transform 0.2s ease;

  .v-icon {
    color: inherit;
    font-size: 23px;
  }

  &:hover {
    border-color: #d0d3d8;
    color: var(--rl-red);
    background: var(--rl-surface-soft);
    transform: rotate(4deg);
  }
}

.recommended-modal__heading {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  align-items: flex-start;
  gap: 16px;

  padding-right: 50px;
}

.recommended-modal__icon {
  width: 52px;
  height: 52px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid var(--rl-border);
  border-radius: 15px;

  color: var(--rl-red);
  background: #ffffff;

  box-shadow: 0 5px 14px rgba(25, 27, 30, 0.06);

  .v-icon {
    color: inherit;
    font-size: 28px;
  }
}

.recommended-modal__heading-content {
  min-width: 0;
}

.recommended-modal__eyebrow,
.section-heading__eyebrow {
  display: block;

  margin-bottom: 4px;

  color: var(--rl-red);

  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 12px;
  line-height: 1.5;
  letter-spacing: 0.75px;
  text-transform: uppercase;
}

.recommended-modal__title {
  margin: 0;

  color: var(--rl-heading);

  font-family: Poppins-Bold, Poppins, sans-serif;
  font-size: 25px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.4px;
}

.recommended-modal__description {
  max-width: 550px;

  margin: 7px 0 0;

  color: var(--rl-muted);

  font-size: 14px;
  line-height: 1.65;
}

/* Step Indicator */

.recommended-steps {
  display: grid;
  grid-template-columns:
    minmax(0, 1fr)
    70px
    minmax(0, 1fr);
  align-items: center;
  gap: 14px;

  margin-top: 24px;
  padding: 15px 17px;

  border: 1px solid var(--rl-border);
  border-radius: 16px;

  background: var(--rl-surface-soft);
}

.recommended-step {
  min-width: 0;

  display: flex;
  align-items: center;
  gap: 12px;

  opacity: 0.55;

  transition: opacity 0.2s ease;

  &--active,
  &--completed {
    opacity: 1;
  }
}

.recommended-step__number {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid var(--rl-border-dark);
  border-radius: 50%;

  color: #6f737a;
  background: #ffffff;

  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 14px;

  .v-icon {
    color: inherit;
    font-size: 19px;
  }
}

.recommended-step--active .recommended-step__number,
.recommended-step--completed .recommended-step__number {
  border-color: var(--rl-red);

  color: #ffffff;
  background: var(--rl-red);

  box-shadow: 0 6px 14px rgba(200, 25, 25, 0.18);
}

.recommended-step__content {
  min-width: 0;

  strong,
  span {
    display: block;
  }

  strong {
    color: #242529;

    font-family: Poppins-SemiBold, Poppins, sans-serif;
    font-size: 13px;
    line-height: 1.45;
  }

  span {
    margin-top: 2px;

    overflow: hidden;

    color: #7d8188;

    font-size: 11px;
    line-height: 1.45;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.recommended-steps__line {
  height: 2px;

  border-radius: 999px;
  background: #d9dce0;

  transition: background-color 0.2s ease;

  &--active {
    background: var(--rl-red);
  }
}

/* Internal Scroll */

.recommended-modal__body {
  min-width: 0;
  min-height: 0;
  flex: 1 1 auto;

  overflow-x: hidden;
  overflow-y: auto;

  overscroll-behavior: contain;

  padding: 25px 30px 30px;

  background: #ffffff;

  scrollbar-width: thin;
  scrollbar-color: #b9bdc3 transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border: 2px solid #ffffff;
    border-radius: 999px;
    background: #b9bdc3;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #989da5;
  }
}

/* Shared Heading */

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.section-heading__title {
  margin: 0;

  color: #202226;

  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 18px;
  line-height: 1.45;
}

.section-heading__description {
  margin: 5px 0 0;

  color: var(--rl-muted);

  font-size: 13px;
  line-height: 1.6;
}

/* Listing Summary */

.listing-summary {
  padding: 20px;

  border: 1px solid var(--rl-border);
  border-radius: 17px;

  background: #ffffff;
}

.listing-summary__type {
  flex: 0 0 auto;

  display: inline-flex;
  align-items: center;
  gap: 7px;

  padding: 8px 12px;

  border: 1px solid var(--rl-border);
  border-radius: 999px;

  color: #45484e;
  background: var(--rl-surface-soft);

  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 12px;

  .v-icon {
    color: var(--rl-red);
    font-size: 17px;
  }
}

.listing-summary__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 11px;

  margin-top: 18px;
}

.listing-summary__item {
  min-width: 0;

  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  align-items: center;
  gap: 11px;

  padding: 13px;

  border: 1px solid #eceef0;
  border-radius: 13px;

  background: var(--rl-surface-soft);
}

.listing-summary__item-icon {
  width: 42px;
  height: 42px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid #e9ebee;
  border-radius: 11px;

  color: #62656b;
  background: #ffffff;

  .v-icon {
    color: inherit;
    font-size: 21px;
  }
}

.listing-summary__item-content {
  min-width: 0;
}

.listing-summary__label,
.listing-summary__value {
  display: block;

  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.listing-summary__label {
  margin-bottom: 3px;

  color: #7e8289;

  font-size: 12px;
  line-height: 1.4;
}

.listing-summary__value {
  color: #2f3135;

  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 14px;
  line-height: 1.5;
}

/* Availability */

.availability-section {
  margin-top: 28px;
}

.availability-section__heading {
  align-items: center;
}

.availability-section__count {
  flex: 0 0 auto;

  display: inline-flex;
  align-items: center;
  gap: 7px;

  padding: 8px 12px;

  border: 1px solid var(--rl-border);
  border-radius: 999px;

  color: #44474c;
  background: var(--rl-surface-soft);

  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 12px;

  .v-icon {
    color: var(--rl-red);
    font-size: 17px;
  }
}

.availability-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 13px;

  margin-top: 18px;
}

.availability-card {
  position: relative;

  min-width: 0;
  min-height: 218px;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  padding: 15px;

  border: 1px solid var(--rl-border);
  border-radius: 16px;

  color: var(--rl-text);
  background: #ffffff;

  font-family: Poppins-Regular, Poppins, sans-serif;
  text-align: left;

  cursor: pointer;
  outline: none;

  box-shadow: 0 4px 12px rgba(27, 29, 33, 0.04);

  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease,
    background-color 0.2s ease;

  &:hover:not(:disabled) {
    box-shadow: 0 12px 25px rgba(31, 33, 37, 0.09);
    transform: translateY(-3px);
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(200, 25, 25, 0.12), 0 10px 24px rgba(31, 33, 37, 0.08);
  }

  &--selected {
    border-color: var(--rl-red);

    background: #ffffff;

    box-shadow: 0 0 0 1px var(--rl-red), 0 10px 23px rgba(200, 25, 25, 0.1);
  }

  &--disabled {
    border-color: #eceef0;

    background: #f5f6f7;

    cursor: not-allowed;
    box-shadow: none;
    opacity: 0.7;
  }
}

.availability-card__header {
  min-height: 34px;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.availability-card__icon {
  width: 34px;
  height: 34px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid var(--rl-border);
  border-radius: 9px;

  color: var(--rl-red);
  background: #ffffff;

  .v-icon {
    color: inherit;
    font-size: 19px;
  }
}

.availability-card__status {
  max-width: calc(100% - 42px);

  display: inline-flex;
  align-items: center;
  gap: 4px;

  padding: 6px 8px;

  overflow: hidden;

  border-radius: 999px;

  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 10px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;

  .v-icon {
    color: inherit;
    font-size: 13px;
  }

  &--selected {
    color: #ffffff;
    background: var(--rl-red);
  }

  &--disabled {
    color: #6f7278;
    background: #e4e6e9;
  }
}

.availability-card__content {
  flex: 1 1 auto;

  display: flex;
  flex-direction: column;
  justify-content: center;

  padding: 14px 0;
}

.availability-card__label {
  display: block;

  margin-bottom: 7px;

  color: #7e8289;

  font-size: 11px;
  line-height: 1.4;
}

.availability-card__date {
  display: block;

  color: #292b2f;

  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 14px;
  line-height: 1.5;
}

.availability-card__separator {
  display: grid;
  grid-template-columns: 1fr 17px 1fr;
  align-items: center;
  gap: 6px;

  margin: 6px 0;

  span {
    height: 1px;
    background: #e9ebee;
  }

  .v-icon {
    color: #adafb5;
    font-size: 15px;
  }
}

.availability-card__footer {
  min-width: 0;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;

  padding-top: 11px;

  border-top: 1px solid #eceef0;
}

.availability-card__remaining {
  min-width: 0;

  display: flex;
  align-items: center;
  gap: 6px;

  color: #6f7379;

  font-size: 11px;
  line-height: 1.45;

  span {
    overflow: hidden;

    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .v-icon {
    flex: 0 0 auto;

    color: #8e9298;
    font-size: 15px;
  }
}

/* Availability price */

.availability-card__price {
  flex: 0 0 auto;

  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;

  text-align: right;
}

.availability-card__price-current {
  display: inline-flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 3px;

  color: #202124;

  strong {
    color: inherit;

    font-family: Poppins-Bold, Poppins, sans-serif;
    font-size: 17px;
    line-height: 1.2;
  }

  span {
    color: inherit;

    font-family: Poppins-SemiBold, Poppins, sans-serif;
    font-size: 10px;
    line-height: 1.3;
  }
}

.availability-card__price-original {
  position: relative;

  display: inline-flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 3px;

  color: var(--rl-red);
  line-height: 1.2;

  &::after {
    content: '';

    position: absolute;
    top: 50%;
    right: -2px;
    left: -2px;

    height: 1px;

    border-radius: 999px;

    background: currentColor;

    transform: translateY(-50%) rotate(-3deg);
  }

  span:first-child {
    font-family: Poppins-SemiBold, Poppins, sans-serif;
    font-size: 12px;
  }

  span:last-child {
    font-family: Poppins-Regular, Poppins, sans-serif;
    font-size: 9px;
  }
}

.availability-empty {
  margin-top: 18px;
  padding: 34px 20px;

  border: 1px dashed #d5d8dc;
  border-radius: 16px;

  background: var(--rl-surface-soft);

  text-align: center;
}

.availability-empty__icon {
  width: 50px;
  height: 50px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid var(--rl-border);
  border-radius: 14px;

  color: #7e8187;
  background: #ffffff;

  .v-icon {
    color: inherit;
    font-size: 26px;
  }
}

.availability-empty h4 {
  margin: 14px 0 5px;

  color: #33353a;

  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 16px;
}

.availability-empty p {
  margin: 0;

  color: var(--rl-muted);

  font-size: 13px;
  line-height: 1.6;
}

/* Confirmation */

.confirmation-section__heading {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  align-items: flex-start;
  gap: 14px;
}

.confirmation-section__heading-icon {
  width: 48px;
  height: 48px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid var(--rl-border);
  border-radius: 14px;

  color: var(--rl-red);
  background: #ffffff;

  box-shadow: 0 4px 12px rgba(31, 33, 37, 0.05);

  .v-icon {
    color: inherit;
    font-size: 24px;
  }
}

/* Selected Period */

.confirmation-periods {
  margin-top: 23px;

  overflow: hidden;

  border: 1px solid var(--rl-border);
  border-radius: 17px;

  background: #ffffff;
}

.confirmation-periods__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;

  padding: 16px 18px;

  border-bottom: 1px solid var(--rl-border);

  background: var(--rl-surface-soft);

  span,
  strong {
    display: block;
  }

  strong {
    color: #292b2f;

    font-family: Poppins-SemiBold, Poppins, sans-serif;
    font-size: 14px;
  }
}

.confirmation-periods__label {
  margin-bottom: 3px;

  color: #777b82;

  font-size: 12px;
}

.confirmation-periods__edit {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  padding: 8px 12px;

  border: 1px solid var(--rl-border-dark);
  border-radius: 9px;

  color: #4f5258;
  background: #ffffff;

  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 12px;

  cursor: pointer;

  transition: color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;

  .v-icon {
    color: var(--rl-red);
    font-size: 15px;
  }

  &:hover {
    border-color: var(--rl-red);
    color: var(--rl-red);
  }
}

.confirmation-periods__list {
  padding: 5px 18px;
}

.confirmation-period {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;

  padding: 15px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #eceef0;
  }
}

.confirmation-period__number {
  width: 34px;
  height: 34px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid var(--rl-border);
  border-radius: 9px;

  color: var(--rl-red);
  background: #ffffff;

  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 13px;
}

.confirmation-period__date {
  min-width: 0;

  span,
  strong {
    display: block;
  }

  span {
    margin-bottom: 3px;

    color: #858990;

    font-size: 11px;
  }

  strong {
    overflow: hidden;

    color: #34363a;

    font-family: Poppins-SemiBold, Poppins, sans-serif;
    font-size: 13px;
    line-height: 1.5;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.confirmation-period__price {
  flex: 0 0 auto;

  text-align: right;

  del {
    display: block;

    margin-top: 2px;

    color: #95999f;

    font-size: 11px;
  }
}

.confirmation-period__current-price {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 4px;

  strong {
    color: var(--rl-red);

    font-family: Poppins-Bold, Poppins, sans-serif;
    font-size: 16px;
  }

  span {
    color: #60646b;
    font-size: 11px;
  }
}

/* Coin Summary */

.coin-summary {
  margin-top: 18px;

  overflow: hidden;

  border: 1px solid var(--rl-border);
  border-radius: 17px;

  background: #ffffff;

  box-shadow: 0 5px 16px rgba(31, 33, 37, 0.035);
}

.coin-summary__header {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  align-items: center;
  gap: 11px;

  padding: 15px 18px;

  border-bottom: 1px solid var(--rl-border);

  background: var(--rl-surface-soft);

  span,
  strong {
    display: block;
  }

  span {
    color: #7c8087;
    font-size: 11px;
  }

  strong {
    margin-top: 2px;

    color: #292b30;

    font-family: Poppins-SemiBold, Poppins, sans-serif;
    font-size: 14px;
  }
}

.coin-summary__header-icon {
  width: 42px;
  height: 42px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid var(--rl-border);
  border-radius: 11px;

  color: var(--rl-red);
  background: #ffffff;

  .v-icon {
    color: inherit;
    font-size: 21px;
  }
}

.coin-summary__rows {
  padding: 13px 18px;

  background: #ffffff;
}

.coin-summary__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  padding: 8px 0;

  color: #656970;

  font-size: 12px;

  strong {
    color: #35373c;

    font-family: Poppins-SemiBold, Poppins, sans-serif;
    font-size: 12px;
  }

  del {
    color: #91959b;
    font-size: 12px;
  }

  &--discount strong {
    color: var(--rl-red);
  }
}

.coin-summary__total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  padding: 17px 18px;

  border-top: 1px solid var(--rl-border);

  background: var(--rl-surface-soft);
}

.coin-summary__total-info {
  span,
  small {
    display: block;
  }

  span {
    color: #292b30;

    font-family: Poppins-SemiBold, Poppins, sans-serif;
    font-size: 13px;
  }

  small {
    margin-top: 4px;

    color: #777b82;

    font-size: 11px;
  }
}

.coin-summary__total-price {
  flex: 0 0 auto;

  display: flex;
  align-items: baseline;
  gap: 5px;

  color: var(--rl-red);

  strong {
    font-family: Poppins-Bold, Poppins, sans-serif;
    font-size: 27px;
    line-height: 1.2;
  }

  span {
    font-family: Poppins-SemiBold, Poppins, sans-serif;
    font-size: 12px;
  }
}

/* Fixed Footer */

.recommended-modal__footer {
  position: relative;
  z-index: 3;

  min-height: 80px;
  flex: 0 0 auto;

  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  padding: 16px 30px;

  border-top: 1px solid var(--rl-border);

  background: #ffffff;

  box-shadow: 0 -8px 20px rgba(31, 33, 37, 0.035);
}

.recommended-modal__selection-info {
  min-width: 0;

  display: flex;
  align-items: center;
  gap: 8px;

  margin-right: auto;

  color: #70747b;

  font-size: 12px;
  line-height: 1.5;

  .v-icon {
    flex: 0 0 auto;

    color: #858990;
    font-size: 18px;
  }
}

.recommended-button {
  min-height: 45px;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;

  padding: 0 19px;

  border-radius: 11px;

  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 13px;

  cursor: pointer;

  transition: border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease,
    box-shadow 0.2s ease, transform 0.2s ease;

  .v-icon {
    color: inherit;
    font-size: 19px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    box-shadow: none;
    transform: none;
  }

  &--primary {
    min-width: 170px;

    border: 1px solid var(--rl-red);

    color: #ffffff;
    background: var(--rl-red);

    box-shadow: 0 9px 19px rgba(200, 25, 25, 0.19);

    &:hover:not(:disabled) {
      border-color: var(--rl-red-dark);

      background: var(--rl-red-dark);

      box-shadow: 0 12px 23px rgba(159, 19, 19, 0.22);

      transform: translateY(-2px);
    }
  }

  &--secondary {
    border: 1px solid var(--rl-border-dark);

    color: #4d5056;
    background: #ffffff;

    &:hover:not(:disabled) {
      border-color: #c5c8cd;

      color: #25272b;
      background: var(--rl-surface-soft);
    }
  }
}

/* Tablet */

@media (max-width: 720px) {
  .recommended-modal {
    height: calc(100vh - 20px);
    min-height: 0;

    border-radius: 18px;
  }

  .recommended-modal__header {
    padding: 23px 21px 20px;
  }

  .recommended-modal__close {
    top: 16px;
    right: 16px;
  }

  .recommended-modal__heading {
    grid-template-columns: 46px minmax(0, 1fr);
    gap: 13px;

    padding-right: 38px;
  }

  .recommended-modal__icon {
    width: 46px;
    height: 46px;

    border-radius: 13px;

    .v-icon {
      font-size: 24px;
    }
  }

  .recommended-modal__title {
    font-size: 21px;
  }

  .recommended-modal__description {
    font-size: 13px;
  }

  .recommended-steps {
    grid-template-columns:
      minmax(0, 1fr)
      30px
      minmax(0, 1fr);

    gap: 9px;

    padding: 13px;
  }

  .recommended-step {
    gap: 9px;
  }

  .recommended-step__number {
    width: 32px;
    height: 32px;
    flex-basis: 32px;

    font-size: 12px;
  }

  .recommended-step__content strong {
    font-size: 12px;
  }

  .recommended-step__content span {
    font-size: 10px;
  }

  .recommended-modal__body {
    padding: 21px 21px 25px;
  }

  .listing-summary {
    padding: 17px;
  }

  .availability-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .recommended-modal__footer {
    padding: 14px 21px;
  }

  .recommended-modal__selection-info {
    display: none;
  }

  .recommended-button--primary {
    margin-left: auto;
  }
}

/* Mobile */

@media (max-width: 480px) {
  .recommended-modal {
    height: calc(100vh - 10px);

    border-radius: 16px;
  }

  .recommended-modal__header {
    padding: 20px 17px 18px;
  }

  .recommended-modal__heading {
    grid-template-columns: 41px minmax(0, 1fr);
    gap: 11px;

    padding-right: 32px;
  }

  .recommended-modal__icon {
    width: 41px;
    height: 41px;

    .v-icon {
      font-size: 22px;
    }
  }

  .recommended-modal__eyebrow,
  .section-heading__eyebrow {
    font-size: 10px;
  }

  .recommended-modal__title {
    font-size: 19px;
  }

  .recommended-modal__description {
    font-size: 12px;
    line-height: 1.6;
  }

  .recommended-modal__close {
    top: 13px;
    right: 13px;

    width: 35px;
    height: 35px;
  }

  .recommended-steps {
    margin-top: 18px;
  }

  .recommended-step__content span {
    display: none;
  }

  .recommended-step__content strong {
    font-size: 11px;
  }

  .recommended-modal__body {
    padding: 18px 17px;
  }

  .section-heading {
    flex-direction: column;
    align-items: stretch;
  }

  .section-heading__title {
    font-size: 16px;
  }

  .section-heading__description {
    font-size: 12px;
  }

  .listing-summary__type,
  .availability-section__count {
    align-self: flex-start;
  }

  .listing-summary__grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .listing-summary__label {
    font-size: 11px;
  }

  .listing-summary__value {
    font-size: 13px;
  }

  .availability-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .availability-card {
    min-height: 202px;
  }

  .availability-card__date {
    font-size: 14px;
  }

  .availability-card__remaining {
    font-size: 11px;
  }

  .availability-card__price-current {
    strong {
      font-size: 18px;
    }

    span {
      font-size: 10px;
    }
  }

  .availability-card__price-original {
    span:first-child {
      font-size: 12px;
    }

    span:last-child {
      font-size: 9px;
    }
  }

  .confirmation-period {
    grid-template-columns: 32px minmax(0, 1fr);
  }

  .confirmation-period__price {
    grid-column: 2;

    text-align: left;

    del {
      text-align: left;
    }
  }

  .confirmation-period__current-price {
    justify-content: flex-start;
  }

  .coin-summary__row {
    font-size: 11px;
  }

  .recommended-modal__footer {
    gap: 8px;

    padding: 12px 17px;
  }

  .recommended-button {
    min-height: 44px;

    padding: 0 15px;

    font-size: 12px;
  }

  .recommended-button--secondary {
    width: 45px;

    padding: 0;

    span {
      display: none;
    }
  }

  .recommended-button--primary {
    min-width: 0;
    flex: 1 1 auto;
  }
}
</style>

<!--
  Global style diperlukan karena wrapper vue-js-modal
  berada di luar elemen scoped component.
-->
<style lang="scss">
.recommended-listing-vmodal {
  .v--modal {
    top: max(16px, calc((100vh - 830px) / 2)) !important;
    height: min(830px, calc(100vh - 32px)) !important;
    max-height: calc(100vh - 32px) !important;

    overflow: hidden !important;

    border-radius: 22px !important;

    background: transparent !important;

    box-shadow: none !important;
  }
}

@media (max-width: 720px) {
  .recommended-listing-vmodal {
    .v--modal {
      top: 10px !important;
      height: calc(100vh - 20px) !important;
      max-height: calc(100vh - 20px) !important;
      border-radius: 18px !important;
    }
  }
}

@media (max-width: 480px) {
  .recommended-listing-vmodal {
    .v--modal {
      top: 5px !important;
      height: calc(100vh - 10px) !important;
      max-height: calc(100vh - 10px) !important;
      border-radius: 16px !important;
    }
  }
}
</style>
